import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Field, FormError } from '@/components/form';
import { DetailList, PageHeader, Section } from '@/components/page';
import { NewPasswordFields, passwordIssue } from '@/features/auth/new-password-fields';
import { RecoveryCodes } from '@/features/auth/recovery-codes';
import { api, errorMessage } from '@/lib/api';
import { useMe } from '@/lib/auth';
import { ROLE_HELP, ROLE_LABEL } from '@/lib/labels';
import { useTheme, type ThemePreference } from '@/lib/theme';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/_app/account')({ component: AccountPage });

function AccountPage() {
  const me = useMe();
  const queryClient = useQueryClient();
  const { preference, setPreference } = useTheme();
  const [name, setName] = useState(me.user.name);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [code, setCode] = useState('');
  const [codes, setCodes] = useState<string[] | null>(null);

  const profile = useMutation({
    mutationFn: () => api.patch('/v1/account/profile', { name: name.trim() }),
    onSuccess: () => {
      toast.success('Name updated');
      void queryClient.invalidateQueries({ queryKey: ['me'] });
    },
  });
  const password = useMutation({
    mutationFn: () => api.post('/v1/account/password', { currentPassword: current, newPassword: next }),
    onSuccess: () => {
      toast.success('Password changed. Other sessions have been signed out.');
      setCurrent('');
      setNext('');
      setConfirm('');
    },
  });
  const regenerate = useMutation({
    mutationFn: () => api.post<{ recoveryCodes: string[] }>('/v1/account/recovery-codes', { code }),
    onSuccess: (res) => {
      setCodes(res.recoveryCodes);
      setCode('');
      void queryClient.invalidateQueries({ queryKey: ['me'] });
    },
  });

  const pwIssue = passwordIssue(next, confirm);

  return (
    <div className="max-w-3xl">
      <PageHeader title="Account & security" />
      <div className="grid gap-5">
        <Section title="Profile">
          <DetailList
            items={[
              { label: 'Email', value: me.user.email },
              { label: 'Role', value: `${ROLE_LABEL[me.user.role]}: ${ROLE_HELP[me.user.role]}` },
              { label: 'Client', value: me.client?.name ?? (me.user.role === 'SUPER_ADMIN' ? 'All clients' : '—') },
            ]}
          />
          <form
            onSubmit={(e: FormEvent) => {
              e.preventDefault();
              profile.mutate();
            }}
            className="flex flex-wrap items-end gap-3 border-t p-5"
          >
            <Field id="account-name" label="Your name" className="min-w-64 flex-1" error={profile.isError ? errorMessage(profile.error) : null}>
              <Input id="account-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={200} />
            </Field>
            <Button type="submit" variant="outline" disabled={!name.trim() || name.trim() === me.user.name || profile.isPending}>
              Save name
            </Button>
          </form>
        </Section>

        <Section title="Password" description="Changing it signs you out everywhere else.">
          <form
            onSubmit={(e: FormEvent) => {
              e.preventDefault();
              if (!pwIssue) password.mutate();
            }}
            className="grid max-w-md gap-4 p-5"
          >
            <FormError message={password.isError ? errorMessage(password.error) : null} />
            <Field id="current-password" label="Current password">
              <Input id="current-password" type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} />
            </Field>
            <NewPasswordFields password={next} confirm={confirm} onPassword={setNext} onConfirm={setConfirm} idPrefix="account" />
            <div>
              <Button type="submit" disabled={!current || !!pwIssue || password.isPending}>
                {password.isPending ? 'Saving…' : 'Change password'}
              </Button>
            </div>
          </form>
        </Section>

        <Section
          title="Two-step verification"
          description={`On. ${me.recoveryCodesRemaining ?? 0} recovery code(s) left. Lost your phone? Ask an administrator to reset it.`}
        >
          <div className="grid gap-4 p-5">
            {codes ? (
              <>
                <p className="text-sm">Your new recovery codes. The old ones no longer work. Save these now; you won’t see them again.</p>
                <RecoveryCodes codes={codes} />
              </>
            ) : (
              <form
                onSubmit={(e: FormEvent) => {
                  e.preventDefault();
                  regenerate.mutate();
                }}
                className="grid max-w-md gap-3"
              >
                <FormError message={regenerate.isError ? errorMessage(regenerate.error) : null} />
                <Field id="regen-code" label="Get new recovery codes" hint="Enter a current code from your authenticator app to confirm.">
                  <Input
                    id="regen-code"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    className="font-mono tracking-[0.3em]"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  />
                </Field>
                <div>
                  <Button type="submit" variant="outline" disabled={code.length !== 6 || regenerate.isPending}>
                    Replace recovery codes
                  </Button>
                </div>
              </form>
            )}
          </div>
        </Section>

        <Section title="Appearance" description="Saved on this browser.">
          <div role="radiogroup" aria-label="Theme" className="flex flex-wrap gap-2 p-5">
            {(['light', 'dark', 'system'] as ThemePreference[]).map((p) => (
              <button
                key={p}
                type="button"
                role="radio"
                aria-checked={preference === p}
                onClick={() => setPreference(p)}
                className={cn(
                  'rounded-lg border px-4 py-2 text-sm focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
                  preference === p ? 'border-primary bg-accent text-accent-foreground' : 'hover:bg-muted',
                )}
              >
                {p === 'light' ? 'Light' : p === 'dark' ? 'Dark' : 'Match system'}
              </button>
            ))}
          </div>
        </Section>
      </div>
    </div>
  );
}
